import { PATH } from '@repo/constants';
import { useAppType, useUserDetailsQuery } from '@repo/lib';
import { AppType } from '@repo/types';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  useSidebar,
} from '@ui/components';
import { cn } from '@ui/lib/utils';
import { ChevronRight, LayoutDashboardIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getNavItemsAdmin } from './config/get-nav-items-admin';
import { getNavItemsPortal } from './config/get-nav-items-portal';
import { Organization } from './ui/organization';
import { User } from './ui/user';

export const AppSidebar = () => {
  const { user } = useUserDetailsQuery();
  const location = useLocation();

  const { open, setOpen, setOpenMobile } = useSidebar();

  const [collapseOpen, setCollapseOpen] = useState<{
    [state: string]: boolean;
  }>({});
  const [lastOpen, setLastOpen] = useState<string>();

  const appType = useAppType();

  const navItems = useMemo(() => {
    if (appType === AppType.PORTAL) {
      return getNavItemsPortal(user?.permissions);
    }
    return getNavItemsAdmin();
  }, [user?.permissions, appType]);

  const isActivePath = useCallback(
    (path: string) =>
      location?.pathname?.split('/')?.slice(0, 3)?.join('/') === path,
    [location?.pathname],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setOpenMobile(false);
  }, []);

  const handleChangeCollapse = useCallback(
    (id: string, open: boolean) => {
      setCollapseOpen((prev) => ({ ...prev, [id]: open }));
      setLastOpen(id);
    },
    [setCollapseOpen],
  );

  useEffect(() => {
    if (!open) {
      setCollapseOpen((prev) => {
        return Object.keys(prev).reduce(
          (acc, key) => {
            acc[key] = key === lastOpen;
            return acc;
          },
          {} as { [key: string]: boolean },
        );
      });
    }
  }, [open]);

  return (
    <Sidebar>
      <SidebarHeader>
        <Organization />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={location.pathname === PATH.DASHBOARD}
                asChild
              >
                <Link
                  className={cn('flex items-center gap-2')}
                  to={PATH.DASHBOARD}
                  onClick={() => setOpenMobile(false)}
                >
                  <LayoutDashboardIcon className="!size-5" />
                  <span>Dashboard</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {navItems.map((group) => {
              return (
                group.children.some((el) => el.allowed) && (
                  <Collapsible
                    key={group.id}
                    className="group/collapsible list-none"
                    open={collapseOpen[group.id]}
                    onOpenChange={(open) =>
                      handleChangeCollapse(group.id, open)
                    }
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <group.icon className="!size-5" />
                          {group.label}
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {group.children
                          .filter((el) => el.allowed)
                          .map((child) => (
                            <SidebarMenuSub key={child.id}>
                              <SidebarMenuSubItem>
                                <SidebarMenuButton
                                  isActive={isActivePath(child.path)}
                                  asChild
                                  onClick={handleClose}
                                >
                                  <Link
                                    className="flex gap-2"
                                    to={child.path}
                                  >
                                    <child.icon className="!size-5" />
                                    {child.label}
                                  </Link>
                                </SidebarMenuButton>
                              </SidebarMenuSubItem>
                            </SidebarMenuSub>
                          ))}
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <User />
      </SidebarFooter>
    </Sidebar>
  );
};
